import { findItem } from './findItem';
import BaseFolderId from 'owa-service/lib/contract/BaseFolderId';
import FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import FolderId from 'owa-service/lib/contract/FolderId';
import SortResults from 'owa-service/lib/contract/SortResults';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import indexedPageView from 'owa-service/lib/factory/indexedPageView';
import { tryConsumeFindMailFolderItemResponse } from 'owa-session-store/lib/services/sessionDataResponseStash';
import getFindItemTraversal from './utils/getFindItemTraversal';
import RequestOptions from 'owa-service/lib/RequestOptions';

/**
 * The FindItemWithStartIndex is used today to load rows when we want to load them from the beginning (or 0th index)
 * This is currently used in load initial rows and in reload scenarios
 * @param baseFolderId the base folder id
 * @param startIndex the startIndex to load the table
 * @param paging the paging type
 * @param viewFilter the view filter
 * @param sortResults the primary and secondary sort by results
 * @param focusedViewFilter the focused view filter
 * @param findItemShapeName - the shape for the request
 * @param shouldTryToUseSessionDataResponse Indicates if we should try to use the result returned in session data response
 * @param options - optional parameters to be passed to request (method and/or headers)
 * @param categoryName - the category name
 * @param hashtagsToInclude the hashtags to include for the request
 * @param hashtagsToExclude the hashtags to exclude for the request
 * @param searchFolderId the search folder id
 * @returns a promise of type FindItemResponseMessage
 */
export default function findItemWithStartIndex(
    baseFolderId: BaseFolderId,
    startIndex: number,
    rowsToLoad: number,
    viewFilter: ViewFilter,
    sortResults: SortResults[],
    focusedViewFilter: FocusedViewFilter,
    findItemShapeName: string,
    shouldTryToUseSessionDataResponse: boolean,
    options: RequestOptions,
    categoryName: string,
    hashtagsToInclude?: string[],
    hashtagsToExclude?: string[],
    searchFolderId?: BaseFolderId
): Promise<FindItemResponseMessage> {
    if (shouldTryToUseSessionDataResponse) {
        const sessionDataFindMailFolderItemResponse = tryConsumeFindMailFolderItemResponse(
            (baseFolderId as FolderId).Id,
            0 /* startIndex */
        );

        if (sessionDataFindMailFolderItemResponse) {
            // Return the session data response if available instead of going to the server
            return Promise.resolve(sessionDataFindMailFolderItemResponse.Body.ResponseMessages
                .Items[0] as FindItemResponseMessage);
        }
    }

    const paging = indexedPageView({
        BasePoint: 'Beginning',
        Offset: 0 /* startIndex */,
        MaxEntriesReturned: rowsToLoad,
    });

    return findItem(
        baseFolderId,
        paging,
        viewFilter,
        sortResults,
        focusedViewFilter,
        findItemShapeName,
        options,
        categoryName,
        getFindItemTraversal(viewFilter),
        hashtagsToInclude,
        hashtagsToExclude,
        searchFolderId
    );
}
